import { useSite } from "@inrange/building-manager-api-client";
import { SiteChangeLog } from "@inrange/building-manager-api-client/models-site";
import { firstNameFromEmail } from "@inrange/theme-components";
import { sortBy } from "@inrange/utils";
import { useMemo, useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import Select from "react-select";
import CollapsibleCard from "../CollapsibleCard";
import {
  ARRAY_DEDUP_FIELDS,
  filterArrayFieldForDiff,
  formatDetail,
  getUpdatedBy,
  getUserNote,
  removeUnchangedArrayItems,
  timeFromEpochString,
} from "./activityLogHelpers";
import { formatFieldName } from "./ActivityLogMap";
import { ChangelogModal } from "./ChangelogModal";

type FilterCategory =
  | "migrations"
  | "fileChanges"
  | "sdmChanges"
  | "ownershipChanges"
  | "recalculations";

const FILTER_OPTIONS: { value: FilterCategory; label: string }[] = [
  { value: "migrations", label: "Migrations" },
  { value: "fileChanges", label: "File changes" },
  { value: "sdmChanges", label: "SDM changes" },
  { value: "ownershipChanges", label: "Ownership changes" },
  { value: "recalculations", label: "Recalculations" },
];

const ALL_CATEGORIES = FILTER_OPTIONS.map((o) => o.value);

const getEntryCategories = (item: SiteChangeLog): Set<FilterCategory> => {
  const categories = new Set<FilterCategory>();

  if (getUpdatedBy(item) === "migrations@inrange.io") {
    categories.add("migrations");
  }

  const userChangeKeys = Object.keys(item.userChange);

  if (userChangeKeys.length === 0 && !categories.has("migrations")) {
    categories.add("recalculations");
  }

  if (userChangeKeys.length === 1 && userChangeKeys[0] === "siteFiles") {
    categories.add("fileChanges");
  }

  if (userChangeKeys.length === 1 && userChangeKeys[0] === "sdmMatches") {
    categories.add("sdmChanges");
  }

  if (userChangeKeys.length === 1 && userChangeKeys[0] === "siteOwnerships") {
    categories.add("ownershipChanges");
  }

  return categories;
};

const getSearchableText = (
  item: SiteChangeLog,
  lossFactorsByKey: Record<string, any>
): string => {
  const parts: string[] = [];

  parts.push(getUpdatedBy(item));

  if (item.userNote) {
    parts.push(item.userNote);
  }

  for (const [fieldName, detail] of Object.entries(item.userChange)) {
    parts.push(formatFieldName(lossFactorsByKey, fieldName));
    const d = detail as { before: any; after: any };
    if (d.before !== undefined && d.before !== null) {
      parts.push(
        typeof d.before === "object"
          ? JSON.stringify(d.before)
          : String(d.before)
      );
    }
    if (d.after !== undefined && d.after !== null) {
      parts.push(
        typeof d.after === "object" ? JSON.stringify(d.after) : String(d.after)
      );
    }
  }

  for (const [fieldName, detail] of Object.entries(item.recalculated)) {
    parts.push(formatFieldName(lossFactorsByKey, fieldName));
    const d = detail as { before: any; after: any };
    if (d.before !== undefined && d.before !== null) {
      parts.push(
        typeof d.before === "object"
          ? JSON.stringify(d.before)
          : String(d.before)
      );
    }
    if (d.after !== undefined && d.after !== null) {
      parts.push(
        typeof d.after === "object" ? JSON.stringify(d.after) : String(d.after)
      );
    }
  }

  return parts.join(" ").toLowerCase();
};

const fieldMatchesSearch = (
  fieldName: string,
  detail: { before: any; after: any },
  searchLower: string,
  lossFactorsByKey: Record<string, any>
): boolean => {
  const parts: string[] = [formatFieldName(lossFactorsByKey, fieldName)];
  if (detail.before !== undefined && detail.before !== null) {
    parts.push(
      typeof detail.before === "object"
        ? JSON.stringify(detail.before)
        : String(detail.before)
    );
  }
  if (detail.after !== undefined && detail.after !== null) {
    parts.push(
      typeof detail.after === "object"
        ? JSON.stringify(detail.after)
        : String(detail.after)
    );
  }
  return parts.join(" ").toLowerCase().includes(searchLower);
};

const passesMinChangeSize = (
  detail: { before: any; after: any },
  minChangeSize: number
): boolean => {
  if (minChangeSize <= 0) return true;
  const before = Number(detail.before);
  const after = Number(detail.after);
  if (isNaN(before) || isNaN(after)) return true;
  return Math.abs(after - before) >= minChangeSize;
};

const getMatchingRecalcFields = (
  item: SiteChangeLog,
  searchLower: string,
  minChangeSize: number,
  lossFactorsByKey: Record<string, any>
): [string, { before: any; after: any }][] => {
  if (!searchLower) return [];
  return Object.entries(
    item.recalculated as Record<string, { before: any; after: any }>
  ).filter(
    ([fieldName, detail]) =>
      fieldMatchesSearch(fieldName, detail, searchLower, lossFactorsByKey) &&
      passesMinChangeSize(detail, minChangeSize)
  );
};

const formatNumericDelta = (detail: {
  before: any;
  after: any;
}): JSX.Element | null => {
  const before = Number(detail.before);
  const after = Number(detail.after);
  if (isNaN(before) || isNaN(after)) return null;
  const delta = after - before;
  if (delta === 0) return null;
  const sign = delta > 0 ? "+" : "";
  const rounded = parseFloat(delta.toPrecision(6));
  let pctStr = "";
  if (before !== 0) {
    const pct = (delta / Math.abs(before)) * 100;
    pctStr = `, ${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;
  }
  return (
    <span style={{ fontSize: "85%", color: "var(--bs-secondary)" }}>
      {" "}
      ({sign}
      {rounded}
      {pctStr})
    </span>
  );
};

const ActivityLog = ({
  siteId,
  createdAt,
  createdBy,
}: {
  siteId: string;
  createdAt: string;
  createdBy: string;
}) => {
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [minChangeSize, setMinChangeSize] = useState(0);
  const [selectedCategories, setSelectedCategories] =
    useState<FilterCategory[]>(ALL_CATEGORIES);
  const [logItem, setLogItem] = useState<SiteChangeLog>({
    siteID: "",
    updatedBy: "",
    sortTime: 0,
    time: 0,
    recalculated: {},
    userChange: {},
  });
  const { LOSS_FACTORS_BY_KEY: lossFactorsByKey, fetchSiteLog } = useSite({
    siteId,
    app: "admin",
  });
  const logData = useMemo(
    () => fetchSiteLog.data?.siteLog || [],
    [fetchSiteLog.data]
  );

  useMemo(() => {
    for (const item of logData) {
      for (const [fieldName, detail] of Object.entries(item.userChange)) {
        if (ARRAY_DEDUP_FIELDS.has(fieldName)) {
          removeUnchangedArrayItems(detail as { before: any[]; after: any[] });
        }
      }
    }
  }, [logData]);

  const sortByFields = ([a, _]: [string, any]) => {
    return formatFieldName(lossFactorsByKey, a);
  };

  const filteredLogData = useMemo(() => {
    const searchLower = searchText.toLowerCase();
    const selectedSet = new Set(selectedCategories);

    return logData.filter((item: SiteChangeLog) => {
      // Category filter: if entry matches categories, at least one must be selected
      const categories = getEntryCategories(item);
      if (categories.size > 0) {
        const hasSelectedCategory = [...categories].some((cat) =>
          selectedSet.has(cat)
        );
        if (!hasSelectedCategory) {
          return false;
        }
      }

      // Search filter
      if (searchLower) {
        const searchableText = getSearchableText(item, lossFactorsByKey);
        if (!searchableText.includes(searchLower)) {
          return false;
        }

        // Min change size filter: among fields matching the search,
        // at least one must have a numeric change >= minChangeSize
        if (minChangeSize > 0) {
          const allFields = [
            ...Object.entries(item.userChange),
            ...Object.entries(item.recalculated),
          ] as [string, { before: any; after: any }][];

          const hasLargeEnoughChange = allFields.some(
            ([fieldName, detail]) =>
              fieldMatchesSearch(
                fieldName,
                detail,
                searchLower,
                lossFactorsByKey
              ) && passesMinChangeSize(detail, minChangeSize)
          );

          if (!hasLargeEnoughChange) {
            return false;
          }
        }
      }

      return true;
    });
  }, [
    logData,
    searchText,
    minChangeSize,
    selectedCategories,
    lossFactorsByKey,
  ]);

  const searchLower = searchText.toLowerCase();

  const activityLogRows = filteredLogData.map(
    (item: SiteChangeLog, index: number) => {
      const matchingRecalcFields = getMatchingRecalcFields(
        item,
        searchLower,
        minChangeSize,
        lossFactorsByKey
      );
      const showDelta = (
        fieldName: string,
        detail: { before: any; after: any }
      ) =>
        searchLower &&
        fieldMatchesSearch(fieldName, detail, searchLower, lossFactorsByKey)
          ? formatNumericDelta(detail)
          : null;

      return (
        <tr
          key={index}
          onClick={() => {
            setLogItem(item), setShowModal(true);
          }}
          style={{
            backgroundColor:
              getUpdatedBy(item) === "migrations@inrange.io"
                ? "#f8f8f8"
                : "white",
            cursor: "pointer",
          }}
        >
          <td
            style={{
              width: "25%",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "normal",
              backgroundColor: "inherit",
            }}
          >
            <div>{getUpdatedBy(item)}</div>
            <div style={{ color: "var(--bs-secondary)", fontSize: "90%" }}>
              {timeFromEpochString(item.time)}
            </div>
            {getUserNote(item) && (
              <div style={{ marginTop: "4px" }}>Note: {getUserNote(item)}</div>
            )}
          </td>
          <td style={{ width: "75%", backgroundColor: "inherit" }}>
            <div
              style={{
                overflowWrap: "anywhere",
                wordBreak: "normal",
              }}
            >
              {Object.entries(
                item.userChange as Record<string, { before: any; after: any }>
              )
                .sort(sortBy(sortByFields))
                .map(([fieldName, detail], index) => {
                  const filtered = ARRAY_DEDUP_FIELDS.has(fieldName)
                    ? filterArrayFieldForDiff(fieldName, detail)
                    : detail;
                  const isEmptyArray = (v: any) =>
                    Array.isArray(v) && v.length === 0;
                  const addedOnly =
                    isEmptyArray(filtered.before) &&
                    !isEmptyArray(filtered.after);
                  const removedOnly =
                    !isEmptyArray(filtered.before) &&
                    isEmptyArray(filtered.after);
                  return (
                    <div key={index}>
                      {formatFieldName(lossFactorsByKey, fieldName)}:{" "}
                      {addedOnly && <>Added: </>}
                      {removedOnly && <>Removed: </>}
                      {fieldName !== "notes" &&
                        !addedOnly &&
                        !removedOnly && (
                          <>
                            {formatDetail(
                              lossFactorsByKey,
                              siteId,
                              formatFieldName,
                              fieldName,
                              filtered.before
                            )}{" "}
                            →{" "}
                          </>
                        )}
                      {removedOnly
                        ? formatDetail(
                            lossFactorsByKey,
                            siteId,
                            formatFieldName,
                            fieldName,
                            filtered.before
                          )
                        : formatDetail(
                            lossFactorsByKey,
                            siteId,
                            formatFieldName,
                            fieldName,
                            filtered.after
                          )}
                      {showDelta(fieldName, detail)}
                    </div>
                  );
                })}
              {matchingRecalcFields.length > 0 && (
                <>
                  <hr style={{ margin: "4px 0" }} />
                  <div
                    style={{
                      fontStyle: "italic",
                      color: "var(--bs-secondary)",
                    }}
                  >
                    Computed:
                  </div>
                  {matchingRecalcFields
                    .sort(sortBy(sortByFields))
                    .map(([fieldName, detail], index) => (
                      <div
                        key={`recalc-${index}`}
                        style={{
                          fontStyle: "italic",
                          color: "var(--bs-secondary)",
                        }}
                      >
                        {formatFieldName(lossFactorsByKey, fieldName)}:{" "}
                        {formatDetail(
                          lossFactorsByKey,
                          siteId,
                          formatFieldName,
                          fieldName,
                          detail.before
                        )}{" "}
                        →{" "}
                        {formatDetail(
                          lossFactorsByKey,
                          siteId,
                          formatFieldName,
                          fieldName,
                          detail.after
                        )}
                        {formatNumericDelta(detail)}
                      </div>
                    ))}
                </>
              )}
            </div>
          </td>
        </tr>
      );
    }
  );

  return (
    <CollapsibleCard title={"Activity log"} className="mt-2">
      <Card.Text className="text-secondary">
        {`Created on ${timeFromEpochString(createdAt)} by ${
          createdBy ?? firstNameFromEmail(createdBy)
        }`}
      </Card.Text>
      {fetchSiteLog.isSuccess && (
        <Row className="mb-3">
          <Col sm={4}>
            <Form.Group>
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search activity log..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Form.Group>
          </Col>
          {searchText && (
            <Col sm={3}>
              <Form.Group>
                <Form.Label>Min change size</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={minChangeSize}
                  onChange={(e) =>
                    setMinChangeSize(
                      Math.max(0, parseFloat(e.target.value) || 0)
                    )
                  }
                />
              </Form.Group>
            </Col>
          )}
          <Col sm={5}>
            <Form.Group>
              <Form.Label>Filter by category</Form.Label>
              <Select
                value={FILTER_OPTIONS.filter((o) =>
                  selectedCategories.includes(o.value)
                )}
                onChange={(values) => {
                  setSelectedCategories(
                    values ? values.map((v) => v.value) : []
                  );
                }}
                options={FILTER_OPTIONS}
                placeholder="All categories filtered out"
                components={{
                  MultiValue: ({ index }) => {
                    if (index > 0) return null;
                    const total = FILTER_OPTIONS.length;
                    const selected = selectedCategories.length;
                    const filtered = total - selected;
                    return (
                      <div
                        style={{
                          marginLeft: "4px",
                          color: "var(--bs-body-color)",
                        }}
                      >
                        {filtered === 0
                          ? "All rows shown"
                          : `${filtered} of ${total} types filtered out`}
                      </div>
                    );
                  },
                }}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border:
                      "var(--bs-border-width) solid var(--bs-border-color)",
                    borderRadius: "var(--bs-border-radius)",
                    color: "var(--bs-body-color)",
                    minWidth: "250px",
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    "::before": {
                      content: state.isSelected ? '"☑"' : '"☐"',
                      fontSize: "1.2em",
                    },
                    backgroundColor: state.isFocused
                      ? "var(--bs-primary-bg-subtle)"
                      : "transparent",
                    color: "var(--bs-body-color)",
                  }),
                }}
                isMulti
                isClearable
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                hideSelectedOptions={false}
              />
            </Form.Group>
          </Col>
        </Row>
      )}
      {fetchSiteLog.isLoading && <div>Loading...</div>}
      {fetchSiteLog.isError && <div>Error loading activity log</div>}
      {fetchSiteLog.isSuccess && (
        <div
          style={{ maxHeight: 900, overflow: "scroll" }}
          className="activity-log-table"
        >
          <Table bordered responsive style={{ fontSize: "90%" }}>
            <thead>
              <tr>
                <th>User / Date</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>{activityLogRows}</tbody>
          </Table>
        </div>
      )}
      {showModal && (
        <ChangelogModal
          logItem={logItem}
          siteId={siteId}
          lossFactorsByKey={lossFactorsByKey}
          onClose={() => setShowModal(false)}
        />
      )}
    </CollapsibleCard>
  );
};

export default ActivityLog;
